import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { loginOrCreateUser, getJourney } from '../services/api'
import type { UserProfile } from '../types'

interface UserState {
  name: string | null
  userId: number | null
  currentYear: number
  yearsWithData: number[]
  isLoading: boolean
  isLoggedIn: boolean
  profile: UserProfile | null
  isKidsMode: boolean

  // Actions
  login: (name: string) => Promise<boolean>
  setName: (name: string) => void
  setProfile: (profile: UserProfile) => void
  updateProfile: (updates: Partial<UserProfile>) => void
  setKidsMode: (isKids: boolean) => void
  clear: () => void
  setYearsWithData: (years: number[]) => void
}

const currentYear = new Date().getFullYear()

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      name: null,
      userId: null,
      currentYear,
      yearsWithData: [],
      isLoading: false,
      isLoggedIn: false,
      profile: null,
      isKidsMode: false,

      login: async (name: string) => {
        set({ isLoading: true })

        const result = await loginOrCreateUser(name)

        if (result) {
          set({
            name: result.user.name,
            userId: result.user.id,
            yearsWithData: result.yearsWithData,
            isLoggedIn: true,
            isLoading: false,
          })
          return true
        }

        // Fallback to local-only mode if API fails
        set({
          name,
          userId: null,
          isLoggedIn: false,
          isLoading: false,
        })
        return false
      },

      setName: (name) => set({ name }),

      setProfile: (profile) => set({ profile }),

      updateProfile: (updates) =>
        set({
          profile: { ...get().profile, ...updates },
        }),

      setKidsMode: (isKids) => set({ isKidsMode: isKids }),

      clear: () =>
        set({
          name: null,
          userId: null,
          yearsWithData: [],
          isLoggedIn: false,
          profile: null,
          isKidsMode: false,
        }),

      setYearsWithData: (years) => set({ yearsWithData: years }),
    }),
    {
      name: 'year-wrapped-user',
    }
  )
)

// Helper to load journey data for a specific year
export async function loadJourneyData(userId: number, year: number) {
  return await getJourney(userId, year)
}
